/**
 * CaseDatabaseUpdatingPage — shown in place of the case while its database is
 * migrated to the current version (started automatically when the case opens).
 *
 * Props:
 *   fromVersion: string — the case database's current version
 *   toVersion:   string — the version it is being updated to
 *   failed:      boolean — the update could not finish; tell the user to contact support
 *   checking:    boolean — the version isn't known yet (the database is still
 *                answering its first queries); show a neutral waiting state
 */

import React from 'react';
import { Card } from '../../design-system';
import styles from './CaseDatabaseUpdatingPage.module.css';

export default function CaseDatabaseUpdatingPage({ fromVersion, toVersion, failed = false, checking = false }) {
  return (
    <div className={styles.page}>
      <Card padding="lg" className={styles.card}>
        {failed ? (
          <div role="alert">
            <h1 className={styles.title}>We couldn't finish updating this case</h1>
            <p className={styles.message}>
              The case database update stopped before it completed.
              Please contact support so we can finish the update.
            </p>
          </div>
        ) : checking ? (
          <div role="status" aria-live="polite">
            <div className={styles.spinner} aria-hidden="true" />
            <h1 className={styles.title}>Opening your case</h1>
            <p className={styles.message}>
              Waiting for the case database to finish starting up.
            </p>
          </div>
        ) : (
          <div role="status" aria-live="polite">
            <div className={styles.spinner} aria-hidden="true" />
            <h1 className={styles.title}>Updating your case database</h1>
            <p className={styles.message}>
              This case is being brought up to the latest version. It will open on its
              own when the update is finished.
            </p>
          </div>
        )}
        {fromVersion && toVersion && (
          <p className={styles.versions}>
            Version {fromVersion} → {toVersion}
          </p>
        )}
      </Card>
    </div>
  );
}
